import React from 'react';
import { Tramitacao } from '../types';
import './Timeline.css';

interface TimelineProps {
  events: Tramitacao[];
  variant?: 'v1' | 'v2' | 'v3' | 'v4';
}

export default function Timeline({ events, variant = 'v1' }: TimelineProps) {
  if (!events || events.length === 0) {
    return <div className="timeline-empty">Nenhum evento de tramitação</div>;
  }

  if (variant === 'v1') return <TimelineV1 events={events} />;
  if (variant === 'v2') return <TimelineV2 events={events} />;
  if (variant === 'v3') return <TimelineV3 events={events} />;
  if (variant === 'v4') return <TimelineV4 events={events} />;

  return null;
}

// V1: Vertical classic timeline
function TimelineV1({ events }: { events: Tramitacao[] }) {
  return (
    <div className="timeline-v1">
      {events.map((event, idx) => (
        <div key={event.id} className="v1-event">
          <div className="v1-circle">{idx + 1}</div>
          <div className="v1-line" />
          <div className="v1-content">
            <h4 className="v1-title">{event.tipo_evento}</h4>
            <p className="v1-local">{event.local}</p>
            {event.detalhes && <p className="v1-detalhes">{event.detalhes}</p>}
            <div className="v1-footer">
              <span className="v1-date">
                {new Date(event.data).toLocaleDateString('pt-BR')}
              </span>
              {event.resultado && (
                <span className={`v1-resultado ${event.resultado}`}>
                  {event.resultado}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// V2: Stepper phases
function TimelineV2({ events }: { events: Tramitacao[] }) {
  const phases = [
    { name: 'Apresentação', icon: '📋' },
    { name: 'Comissões', icon: '👥' },
    { name: 'Plenário', icon: '🏛️' },
    { name: 'Votação', icon: '🗳️' },
    { name: 'Lei', icon: '✅' },
  ];

  const currentPhaseIdx = Math.min(
    Math.floor((events.length / 3) * 4),
    phases.length - 1
  );

  return (
    <div className="timeline-v2">
      <div className="v2-stepper">
        {phases.map((phase, idx) => (
          <div
            key={phase.name}
            className={`v2-phase ${idx <= currentPhaseIdx ? 'active' : ''}`}
          >
            <div className="v2-phase-circle">{phase.icon}</div>
            <span className="v2-phase-name">{phase.name}</span>
          </div>
        ))}
      </div>

      <div className="v2-progress-bar">
        <div
          className="v2-progress-fill"
          style={{
            width: `${((currentPhaseIdx + 1) / phases.length) * 100}%`,
          }}
        />
      </div>

      <div className="v2-events-list">
        {events.map((event) => (
          <div key={event.id} className="v2-event">
            <span className="v2-event-type">{event.tipo_evento}</span>
            <span className="v2-event-local">{event.local}</span>
            <span className="v2-event-date">
              {new Date(event.data).toLocaleDateString('pt-BR')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// V3: Swimlanes (Câmara / Senado)
function TimelineV3({ events }: { events: Tramitacao[] }) {
  const cameraEvents = events.filter(
    (e) =>
      e.local.toLowerCase().includes('câmara') ||
      e.local.toLowerCase().includes('comissão')
  );
  const senadoEvents = events.filter(
    (e) =>
      e.local.toLowerCase().includes('senado') ||
      e.local.toLowerCase().includes('plenário')
  );

  return (
    <div className="timeline-v3">
      <div className="v3-swimlanes">
        <div className="v3-lane">
          <h4 className="v3-lane-title">Câmara dos Deputados</h4>
          <div className="v3-events">
            {cameraEvents.length > 0 ? (
              cameraEvents.map((event) => (
                <div key={event.id} className="v3-event">
                  <div className="v3-event-marker" />
                  <div className="v3-event-content">
                    <span className="v3-event-tipo">{event.tipo_evento}</span>
                    <span className="v3-event-date">
                      {new Date(event.data).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="v3-empty">Nenhum evento</div>
            )}
          </div>
        </div>

        <div className="v3-lane">
          <h4 className="v3-lane-title">Senado Federal</h4>
          <div className="v3-events">
            {senadoEvents.length > 0 ? (
              senadoEvents.map((event) => (
                <div key={event.id} className="v3-event">
                  <div className="v3-event-marker" />
                  <div className="v3-event-content">
                    <span className="v3-event-tipo">{event.tipo_evento}</span>
                    <span className="v3-event-date">
                      {new Date(event.data).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="v3-empty">Nenhum evento</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// V4: Table with sorting
function TimelineV4({ events }: { events: Tramitacao[] }) {
  const [sortBy, setSortBy] = React.useState<'data' | 'tipo'>('data');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');

  const sortedEvents = React.useMemo(() => {
    const sorted = [...events];
    sorted.sort((a, b) => {
      let aVal: any = a[sortBy === 'data' ? 'data' : 'tipo_evento'];
      let bVal: any = b[sortBy === 'data' ? 'data' : 'tipo_evento'];

      if (sortBy === 'data') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [events, sortBy, sortDir]);

  const handleSort = (col: 'data' | 'tipo') => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('desc');
    }
  };

  return (
    <div className="timeline-v4">
      <table className="v4-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('data')} className="v4-sortable">
              Data
              {sortBy === 'data' && (
                <span className="v4-sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>
              )}
            </th>
            <th onClick={() => handleSort('tipo')} className="v4-sortable">
              Evento
              {sortBy === 'tipo' && (
                <span className="v4-sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>
              )}
            </th>
            <th>Local</th>
            <th>Resultado</th>
          </tr>
        </thead>
        <tbody>
          {sortedEvents.map((event) => (
            <tr key={event.id}>
              <td className="v4-date">
                {new Date(event.data).toLocaleDateString('pt-BR')}
              </td>
              <td className="v4-tipo">{event.tipo_evento}</td>
              <td className="v4-local">{event.local}</td>
              <td className="v4-resultado">
                {event.resultado ? (
                  <span className={`v4-resultado-badge ${event.resultado}`}>
                    {event.resultado}
                  </span>
                ) : (
                  <span className="v4-pendente">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
